import { useMemo, useState } from 'react'

import { zodResolver } from '@hookform/resolvers/zod'
import { FormProvider, useForm } from 'react-hook-form'

import { Button } from '@/components/ui/button.tsx'
import { Dialog } from '@/components/ui/dialog.tsx'
import {
  funnelTrackingFormSchema,
  getInitialValues,
} from '@/dialogs/FunnelTrackingCreateDialog/validation.ts'
import type { DialogProps } from '@/dialogs/types'
import {
  type FunnelCheck,
  type FunnelCheckEdit,
} from '@/modules/tracking/funnel/types.ts'
import { extractUsername } from '@/utils/socials.ts'

import { FunnelTrackingForm } from '../../modules/tracking/funnel/components/forms/FunnelTrackingForm.tsx'

interface Props extends DialogProps {
  item?: FunnelCheck
  onSubmit: (body: FunnelCheckEdit) => Promise<unknown>
  creatorName: string | null
}

export const FunnelTrackingEditDialog = ({
  item,
  creatorName,
  isOpen,
  onClose,
  onSubmit,
}: Props) => {
  const [isLoading, setIsLoading] = useState(false)

  const form = useForm({
    defaultValues: getInitialValues(creatorName, item),
    resolver: zodResolver(funnelTrackingFormSchema),
  })

  const handleSubmit = useMemo(
    () =>
      form.handleSubmit(async (body: FunnelCheckEdit) => {
        setIsLoading(true)
        try {
          const values = {
            ...body,
            entryPointData: body.entryPointData
              ? extractUsername(body.entryPointData)
              : body.entryPointData,
          }

          await onSubmit(values)

          onClose()
        } catch (error) {
          /* empty */
        } finally {
          setIsLoading(false)
        }
      }),
    [form, onSubmit, onClose],
  )

  return (
    <Dialog
      show={isOpen}
      onClose={onClose}
    >
      <FormProvider {...form}>
        <form onSubmit={handleSubmit}>
          <Dialog.Header>
            <Dialog.Title>Funnel Check</Dialog.Title>
          </Dialog.Header>
          <Dialog.Description className="pt-6">
            <div className="py-5">
              <FunnelTrackingForm />
            </div>
          </Dialog.Description>

          <Dialog.ClosableFooter
            onClose={onClose}
            className="mt-6"
          >
            <Button
              variant="default"
              onClick={handleSubmit}
              isLoading={isLoading}
            >
              Save
            </Button>
          </Dialog.ClosableFooter>
        </form>
      </FormProvider>
    </Dialog>
  )
}
